import { useState, useEffect } from 'react';
import { Box, Paper, Typography, Chip, Grid, CircularProgress, Alert, IconButton, Menu, MenuItem, Divider } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import logo from '../assets/logo.jpeg';
import { getSystems } from '../api/systems';

function AtoChip({ status }) {
  const color = status === 'ACTIVE' ? '#4caf50' : status === 'EXPIRED' ? '#f44336' : '#ff9800';
  return <Chip label={status || 'PENDING'} size="small" sx={{ backgroundColor: color, color: '#fff', fontWeight: 700 }} />;
}

export default function SystemSelect({ onSelect, currentUser, onLogout }) {
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    getSystems()
      .then(data => setSystems(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Failed to load systems'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 6, position: 'relative' }}>

      {/* Top-right user menu */}
      <Box sx={{ position: 'absolute', top: 16, right: 24 }}>
        <IconButton onClick={e => setAccountAnchor(e.currentTarget)} sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}>
          <AccountCircleIcon sx={{ fontSize: 30 }} />
        </IconButton>
        <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)}
          PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', color: '#ffffff' } }}>
          <MenuItem disabled sx={{ color: '#7a8fa6', fontSize: 12 }}>
            {currentUser ? `${currentUser.username} · ${currentUser.role}` : 'Signed in'}
          </MenuItem>
          <Divider sx={{ borderColor: '#1e3a5f' }} />
          <MenuItem onClick={() => { setAccountAnchor(null); onLogout?.(); }}
            sx={{ color: '#f44336', '&:hover': { backgroundColor: '#2a0a0a' } }}>
            Sign Out
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
        <img src={logo} alt="Eighth Technologies" style={{ width: 48, height: 48, borderRadius: '50%' }} />
        <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, letterSpacing: 3 }}>ASPYN</Typography>
      </Box>
      <Typography variant="body1" sx={{ color: '#7a8fa6', marginBottom: 6 }}>
        Select a system to view
      </Typography>

      {loading && <CircularProgress sx={{ color: '#00bcd4' }} />}

      {error && (
        <Alert severity="error" sx={{ backgroundColor: '#2a0a0a', color: '#f44336', maxWidth: 500 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && systems.length === 0 && (
        <Alert severity="info" sx={{ backgroundColor: '#0d1f33', color: '#7a8fa6', maxWidth: 500 }}>
          No systems found. Ask an administrator to create a system and grant you access.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ maxWidth: 1100 }}>
        {systems.map((system) => (
          <Grid item xs={12} md={4} key={system.id}>
            <Paper
              onClick={() => onSelect(system)}
              sx={{
                backgroundColor: '#111827',
                border: '1px solid #1e3a5f',
                borderRadius: 2,
                padding: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '1px solid #00bcd4',
                  backgroundColor: '#0d1f33',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ marginBottom: 2 }}>
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700 }}>{system.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                  {system.cage_code && (
                    <Typography variant="caption" sx={{ color: '#7a8fa6' }}>CAGE: {system.cage_code}</Typography>
                  )}
                  {system.classification_level && (
                    <Chip label={system.classification_level} size="small" sx={{ backgroundColor: '#1e3a5f', color: '#c0cfe0', fontSize: 10 }} />
                  )}
                </Box>
              </Box>

              <Box sx={{ height: '1px', backgroundColor: '#1e3a5f', marginBottom: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                {system.ato_status !== undefined && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VerifiedIcon sx={{ color: system.ato_status === 'ACTIVE' ? '#4caf50' : '#ff9800', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ color: '#7a8fa6' }}>ATO Status</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <AtoChip status={system.ato_status} />
                      {system.ato_expires && (
                        <Typography variant="caption" sx={{ color: '#7a8fa6', display: 'block' }}>
                          Expires {system.ato_expires}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon sx={{ color: '#00bcd4', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#7a8fa6' }}>Your Role</Typography>
                  </Box>
                  <Chip
                    label={system.user_role || 'admin'}
                    size="small"
                    sx={{ backgroundColor: '#1e3a5f', color: '#00bcd4', fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}
                  />
                </Box>

                {system.description && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: '#00bcd4', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ color: '#7a8fa6' }}>Description</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#c0cfe0', maxWidth: 160, textAlign: 'right' }}>
                      {system.description}
                    </Typography>
                  </Box>
                )}

              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

    </Box>
  );
}
